import React, { useContext, useEffect, useRef, useState } from 'react'
import { Button, Dimmer, Icon, Loader } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { ThemeContext } from 'styled-components'
import { b64toBlob, formatFileSize } from '~/utils/helpers'
import Word from '@assets/images/Word.png'
import Excel from '@assets/images/Excel.png'
import Documents from '@assets/images/Documents.png'
import notify from '~/components/Notify'
import { imageTypes, pdfType, videoTypes } from '~/Constants'
import { AcceptInputProp } from '~/types'
import { AssetUpload } from './style'

interface AssetFileUploadProps {
  onChange?: (file: File) => void
  id?: string
  value?: any | File
  activeTabIndex?: number
  sizeLimitInMB?: number
  accept?: AcceptInputProp[]
  mimeTypes?: any
  removeButton?: () => void
  removeDetails?: boolean
}

const ONE_MILLION = 1000000

// Asset upload used by the login popup editor.
// Replicates the behaviour/interface of LargeFileUpload, but renders the
// "Upload your asset" drop-zone design.
const AssetFileUpload: React.FC<AssetFileUploadProps> = (props) => {
  const { onChange, mimeTypes } = props
  const { t } = useTranslation()
  const { firstColor } = useContext(ThemeContext)
  const [file, setFile] = useState<File | null>(props.value)
  const [previewUrl, setPreviewUrl] = useState<string | ArrayBuffer | null>(null)
  const [loadingFile, setLoadingFile] = useState(false)
  const refIframe = useRef(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (typeof props.value === 'string' && props.value.startsWith('http')) {
      const urlPath = props.value.split('?')[0]
      const filename = urlPath.split('/').pop() || ''
      const ext = filename.split('.').pop() || ''
      setPreviewUrl(props.value)
      setFile({ name: filename, type: getType(ext) } as File)
    } else if (props.value?.file && typeof props.value.file === 'string' && props.value.file.startsWith('http')) {
      const filename = props.value.content || ''
      const ext = filename.split('.').pop() || ''
      setPreviewUrl(props.value.file)
      setFile({ name: filename, type: getType(ext) } as File)
    }
  }, [props.activeTabIndex, props.id, typeof props.value === 'string' ? props.value : props.value?.src ?? props.value?.file])

  useEffect(() => {
    if (file?.type?.startsWith('application/pdf') && !file?.lastModified && previewUrl) {
      handleRefreshClick()
    }
  }, [previewUrl])

  function handleRefreshClick() {
    if (refIframe.current) {
      refIframe.current.setAttribute('src', '')
      const encodedUrl = encodeURIComponent(previewUrl.toString())
      refIframe.current.setAttribute('src', `https://docs.google.com/gview?url=${encodedUrl}&embedded=true`)
    }
  }

  function getType(type: string) {
    if (imageTypes.find(typeI => typeI === type)) {
      return `image/${type}`
    } else if (videoTypes.find(typeI => typeI === type)) {
      return `video/${type}`
    } else if (pdfType === type) {
      return `application/${type}`
    } else if (type === 'xlxs' || type === 'docx') {
      return `application/${type}`
    }
    return type
  }

  const pickFileHandler = () => {
    fileInputRef.current?.click()
  }

  function applyPickedFileAsPreview(pickedFile: File) {
    onChange(pickedFile)
    setFile(pickedFile)
    const fileReader = new FileReader()
    fileReader.onload = () => {
      if (pickedFile.type.startsWith('application/pdf')) {
        const blob = b64toBlob(fileReader.result, pickedFile.type)
        const blobUrl = URL.createObjectURL(blob)
        setPreviewUrl(blobUrl)
      } else {
        setPreviewUrl(fileReader.result)
      }
    }
    if (pickedFile.type.startsWith('image')) {
      fileReader.readAsDataURL(pickedFile)
    } else if (pickedFile.type.startsWith('video')) {
      setPreviewUrl(URL.createObjectURL(pickedFile))
    } else if (pickedFile.type.startsWith('application')) {
      fileReader.readAsDataURL(pickedFile)
    } else {
      fileReader.readAsArrayBuffer(pickedFile)
    }
  }

  function validateAndApply(pickedFile: File | null) {
    if (!pickedFile) {
      return
    }
    if (props.sizeLimitInMB && (pickedFile.size / ONE_MILLION) > props.sizeLimitInMB) {
      notify(t('File size is too large'), 'error')
      return
    }
    const separatorSize = pickedFile?.name?.split('.')
    const basicType = pickedFile?.name?.split('.')[separatorSize.length - 1]
    if (mimeTypes && !mimeTypes.find(mimeType => mimeType === basicType)) {
      notify(t('File type not supported'), 'error')
      return
    }
    applyPickedFileAsPreview(pickedFile)
  }

  const fileChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    event.stopPropagation()
    validateAndApply(event.target.files?.[0])
  }

  function renderApplicationDocuments() {
    if (file?.type.startsWith('application/pdf')) {
      if (!file.lastModified) {
        const encodedUrl = encodeURIComponent(previewUrl.toString())
        return <iframe ref={refIframe} src={`https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`} width='100%' height='100%' />
      }
      return <iframe src={previewUrl.toString()} />
    } else if (file?.name.includes('.docx')) {
      return <img src={Word} alt='Preview' />
    } else if (file?.name.includes('.xlsx')) {
      return <img src={Excel} alt='Preview' />
    }
    return <img src={Documents} alt='Preview' />
  }

  function renderPreview() {
    const basicType = file?.type?.split('/')[0]
    switch (basicType) {
      case 'image':
        return <img src={previewUrl as string} alt='Preview' />
      case 'video':
        return <video src={previewUrl as string} controls />
      case 'application':
        return renderApplicationDocuments()
      default:
        return <img src={Documents} alt='Preview' />
    }
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.classList.remove('drag-over')
    validateAndApply(event.dataTransfer.files[0])
  }

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.classList.add('drag-over')
  }

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.classList.remove('drag-over')
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  function renderDropzone() {
    const supportedTypes = props?.accept?.join(', ') || props?.mimeTypes?.join(', ') || '.PDF, .DOC, .DOCX, .TXT, .CSV, .XLS, .XLSX, .PNG, .JPEG, .JPG, .BMP, .MP4'
    return <AssetUpload.Dropzone
      onClick={pickFileHandler}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <AssetUpload.DropzoneInner>
        <Dimmer inverted active={loadingFile}>
          <Loader active={loadingFile} />
        </Dimmer>
        <AssetUpload.IconBadge>
          <Icon name='cloud upload' style={{ color: firstColor }} />
        </AssetUpload.IconBadge>
        <AssetUpload.Title>{t('Upload your asset')}</AssetUpload.Title>
        <AssetUpload.Subtitle>{t('Drag and drop your file here, or click the button below')}</AssetUpload.Subtitle>
        <AssetUpload.SupportedTypes>
          {t('Supported file types: {{supportedTypes}}', { supportedTypes })}
        </AssetUpload.SupportedTypes>
      </AssetUpload.DropzoneInner>
    </AssetUpload.Dropzone>
  }

  function renderPreviewCard() {
    return <AssetUpload.PreviewCard>
      <AssetUpload.PreviewToolbar>
        {!props.removeDetails && <div className='file-meta'>
          <span className='file-name'>{t('File name: {{fileName}}', { fileName: file?.name })}</span>
          {file?.size ? <span className='file-size'>{t('File size: {{fileSize}}', { fileSize: formatFileSize(file?.size) })}</span> : null}
        </div>}
        <div className='actions'>
          <Button
            type={'button'}
            onClick={pickFileHandler}
            basic
            size={'small'}
            icon={'exchange'}
            content={t('Change file')}
          />
          {props.removeButton && <Button
            type={'button'}
            onClick={() => {
              props.removeButton()
              onChange(null)
              setFile(null)
              setPreviewUrl('')
            }}
            basic
            color={'red'}
            size={'small'}
            icon={'trash'}
            content={t('Remove')}
          />}
        </div>
      </AssetUpload.PreviewToolbar>
      <AssetUpload.PreviewBody>
        <Dimmer inverted active={loadingFile}>
          <Loader active={loadingFile} />
        </Dimmer>
        {renderPreview()}
      </AssetUpload.PreviewBody>
    </AssetUpload.PreviewCard>
  }

  const effectivePreviewUrl = previewUrl ||
    (typeof props.value === 'string' && props.value.startsWith('http') ? props.value : null) ||
    (typeof props.value?.file === 'string' && props.value.file.startsWith('http') ? props.value.file : null)

  return <AssetUpload.Container>
    <AssetUpload.FileInput
      ref={fileInputRef}
      type='file'
      accept={props?.accept?.join(',') || 'all'}
      onChange={fileChangeHandler}
    />
    {effectivePreviewUrl ? renderPreviewCard() : renderDropzone()}
  </AssetUpload.Container>
}

export default AssetFileUpload
